import React, { useMemo } from 'react';

import { Typography } from 'antd';

import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { Tree } from '@/shared/ui/Tree';

import {
  useAssetTreeAutoSelect,
  useAssetTreeData,
  useAssetTreeExpand,
  useAssetTreeRender,
  useAssetTreeSelection,
} from '@/features/discovery';

import styles from './AssetTree.module.css';

const AssetTreeComponent: React.FC = () => {
  const { treeData: initialTreeData, isLoading: isInitialLoading } = useAssetTreeData();

  const { treeData, expandedKeys, loadingKeys, onExpand, collapseAll } = useAssetTreeExpand({
    initialTreeData,
  });

  const { selectedKeys, onSelect } = useAssetTreeSelection({ treeData });

  const { enrichTreeData } = useAssetTreeRender();

  const enrichedTreeData = useMemo(() => enrichTreeData(treeData, styles), [treeData, enrichTreeData]);

  useAssetTreeAutoSelect({
    treeData,
    expandedKeys,
    onExpand,
    onSelect,
  });

  if (isInitialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h5 className={styles.title}>데이터 자산</h5>
          </div>
        </div>
        <div className={styles.emptyState}>
          <Typography.Text type="secondary">로딩 중...</Typography.Text>
        </div>
      </div>
    );
  }

  if (treeData.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h5 className={styles.title}>데이터 자산</h5>
          </div>
        </div>
        <div className={styles.emptyState}>
          <Typography.Text type="secondary">데이터 자산이 없습니다</Typography.Text>
        </div>
      </div>
    );
  }

  const hasExpandedNodes = expandedKeys.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h5 className={styles.title}>데이터 자산</h5>
          {hasExpandedNodes && (
            <IconButton onClick={collapseAll} className={styles.iconButton} tooltip="모두 닫기">
              <Icon name="arrow-short-down" size={16} />
            </IconButton>
          )}
        </div>
      </div>
      <div className={styles.treeContainer}>
        <Tree
          treeData={enrichedTreeData}
          expandedKeys={expandedKeys}
          selectedKeys={selectedKeys}
          loadingKeys={loadingKeys}
          onSelect={onSelect}
          onExpand={onExpand}
        />
      </div>
    </div>
  );
};

export const AssetTree = React.memo(AssetTreeComponent);
